import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ServiceWaitingListModalStore } from './service-waiting-list-modal.store';
import { dateify, dateifyOrNow } from '../../../../../shared/helpers/date.helper';
import { contentGlobalSuccess } from '../../../../content.error';
import { fb, routeParams } from '../../../../../shared/shared.global-service';
import { Dialog } from '@openng/optimus-ui/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import {
    ServiceWaitingListObject,
} from '@libs/types/src/services/object-type/service/settings/service-waiting-list.object';
import { serviceWaitingListUnitEnumOptions } from '@libs/types/src/services/enum/service-waiting-list-unit.enum';
import {
    ServiceWaitingListCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-waiting-list-create.input';
import {
    ServiceWaitingListUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-waiting-list-update.input';

@Component({
    selector: 'app-service-waiting-list-modal',
    templateUrl: 'service-waiting-list-modal.component.html',
    styleUrl: 'service-waiting-list-modal.component.scss',
    providers: [ServiceWaitingListModalStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        Dialog,
        TranslatePipe,
        DateInputComponent,
        SelectInputComponent,
        TextInputComponent,
        CheckInputComponent,
        ActionButtonsComponent,
    ],
})
export class ServiceWaitingListModalComponent extends SubscriptionComponent {
    public readonly store = inject(ServiceWaitingListModalStore);

    @Input() waitingListItem: ServiceWaitingListObject | null = null;

    protected visible = false;

    protected readonly initialState = {
        dateFrom: new Date(),
        required: true,
    };

    protected form = fb.group({
        name: fb.control<string | null>(null, Validators.required),
        type: fb.control<string | null>(null, Validators.required),
        dateFrom: fb.control<Date>(this.initialState.dateFrom, Validators.required),
        dateTo: fb.control<Date | null>(null),
        description: fb.control<string | null>(null),

        required: fb.control<boolean>(this.initialState.required),
    });

    public show(visible: boolean = true, item: ServiceWaitingListObject | null = null): void {
        this.visible = visible;
        this.waitingListItem = item;
        if (item) {
            this.form.patchValue({
                ...item,
                dateFrom: dateifyOrNow(item.dateFrom),
                dateTo: dateify(item.dateTo),
            });
        } else {
            this.form.reset(this.initialState);
        }
    }

    protected async submit(): Promise<void> {
        if (isInvalid(this.form)) return;

        if (!this.waitingListItem) {
            const serviceId = Number(routeParams.serviceId());
            await this.store.api.createServiceWaitingListItem({
                serviceId,
                ...this.form.value,
                dateFrom: this.form.value.dateFrom?.toISOString(),
                dateTo: this.form.value.dateTo?.toISOString(),
                description: this.form.value.description || '',
            } as ServiceWaitingListCreateInput);
            contentGlobalSuccess.next('common.created');
        } else {
            await this.store.api.updateServiceWaitingListItem({
                id: this.waitingListItem!.id,
                ...this.form.value,
                dateFrom: this.form.value.dateFrom?.toISOString(),
                dateTo: this.form.value.dateTo?.toISOString(),
                description: this.form.value.description || '',
            } as ServiceWaitingListUpdateInput);
            contentGlobalSuccess.next('common.updated');
        }

        this.show(false);
    }

    protected readonly serviceWaitingListUnitEnumOptions = serviceWaitingListUnitEnumOptions;
}
